import React, { createContext, useContext, useMemo } from 'react';

export type PreviewDataMode = 'layout' | 'mock';

/**
 * Callback provided by the studio to generate mock data for a specific block.
 * Blocks call this with their schema files and receive mock data back.
 * This keeps mock generation in the studio — engine libraries stay clean.
 */
export type MockDataResolver = (
  blockId: string,
  rawDomain: Record<string, unknown>,
) => Record<string, unknown>;

export interface StudioPreviewConfig {
  /** When true, data-fetch actions are skipped and a placeholder is shown instead. */
  isStudioPreview: boolean;
  /** Controls whether blocks show empty fields ('layout') or sample data ('mock'). Defaults to 'mock'. */
  previewDataMode: PreviewDataMode;
  /** Pre-generated mock data bag that block renderers can use when previewDataMode is 'mock'. */
  mockDataBag?: Record<string, unknown>;
  /**
   * Studio-provided callback that generates mock data for a specific block.
   * When present, block renderers call this instead of relying on mockDataBag.
   * This allows the studio to generate per-block mock data without the engine
   * knowing anything about mock data generation.
   */
  resolveMockData?: MockDataResolver;
}

const defaultConfig: StudioPreviewConfig = {
  isStudioPreview: false,
  previewDataMode: 'mock',
};

const StudioPreviewContext = createContext<StudioPreviewConfig>(defaultConfig);

export function StudioPreviewProvider({
  children,
  isStudioPreview,
  previewDataMode = 'mock',
  mockDataBag,
  resolveMockData,
}: {
  children: React.ReactNode;
  isStudioPreview: boolean;
  previewDataMode?: PreviewDataMode;
  mockDataBag?: Record<string, unknown>;
  resolveMockData?: MockDataResolver;
}): JSX.Element {
  const value = useMemo(
    () => ({ isStudioPreview, previewDataMode, mockDataBag, resolveMockData }),
    [isStudioPreview, previewDataMode, mockDataBag, resolveMockData],
  );
  return (
    <StudioPreviewContext.Provider value={value}>
      {children}
    </StudioPreviewContext.Provider>
  );
}

export function useStudioPreview(): StudioPreviewConfig {
  return useContext(StudioPreviewContext);
}
